import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const faqCategories = [
    {
      title: 'Project Collaboration',
      icon: 'Code',
      questions: [
        {
          question: 'What types of projects do you work on?',
          answer: `I specialize in full-stack web applications, mobile apps, and technical consulting. My expertise includes React, Node.js, Python, and cloud architecture. I work on everything from startup MVPs to enterprise-scale applications, with a focus on performance, scalability, and user experience.`
        },
        {
          question: 'What is your typical project timeline?',
          answer: `Project timelines vary based on complexity:\n• Simple websites: 2-4 weeks\n• Web applications: 6-12 weeks\n• Complex systems: 3-6 months\n\nI provide detailed project plans with milestones and regular progress updates. Rush projects are available with adjusted pricing.`
        },
        {
          question: 'How do you handle project pricing?',
          answer: `I offer both fixed-price and hourly arrangements:\n• Fixed-price: Best for well-defined projects with clear scope\n• Hourly: $150/hour for ongoing work and consultations\n• Retainer: Monthly arrangements for long-term partnerships\n\nAll projects include detailed proposals with transparent pricing.`
        },
        {
          question: 'Do you provide ongoing support after project completion?',
          answer: `Yes! I offer comprehensive post-launch support:\n• 30 days of free bug fixes and minor adjustments\n• Ongoing maintenance packages available\n• Performance monitoring and optimization\n• Feature updates and enhancements\n• Technical documentation and training`
        }
      ]
    },
    {
      title: 'Mentorship Program',
      icon: 'Users',
      questions: [
        {
          question: 'How does your mentorship program work?',
          answer: `My mentorship program is designed for personalized growth:\n• 1-on-1 sessions via video call\n• Customized learning paths based on your goals\n• Code reviews and project guidance\n• Career advice and industry insights\n• Access to exclusive resources and community\n\nSessions are typically 45-60 minutes, scheduled weekly or bi-weekly.`
        },
        {
          question: 'What skill levels do you mentor?',
          answer: `I work with developers at all stages:\n• Complete beginners starting their coding journey\n• Bootcamp graduates transitioning to professional roles\n• Junior developers looking to advance their careers\n• Mid-level developers aiming for senior positions\n\nEach program is tailored to your current level and goals.`
        },
        {
          question: 'What is the time commitment for mentorship?',
          answer: `Flexible arrangements to fit your schedule:\n• Minimum: 1 session per month\n• Recommended: 2-4 sessions per month\n• Intensive: Weekly sessions for accelerated growth\n• Self-paced with on-demand support available\n\nYou can adjust frequency based on your progress and availability.`
        },
        {
          question: 'How much does mentorship cost?',
          answer: `Mentorship pricing is designed to be accessible:\n• Single session: $100 (45-60 minutes)\n• Monthly package: $350 (4 sessions + email support)\n• Quarterly program: $900 (12 sessions + resources)\n• Scholarship opportunities available for underrepresented groups`
        }
      ]
    },
    {
      title: 'Speaking & Events',
      icon: 'Mic',
      questions: [
        {
          question: 'What topics do you speak about?',
          answer: `I cover a wide range of technical and leadership topics:\n• Modern web development and React ecosystem\n• Building and scaling development teams\n• Community building and developer advocacy\n• Career growth and technical leadership\n• Open source contribution and collaboration\n• Mentorship and knowledge sharing`
        },
        {
          question: 'Do you speak at virtual events?',
          answer: `Absolutely! I'm experienced with virtual presentations:\n• Webinars and online conferences\n• Interactive workshops and tutorials\n• Panel discussions and Q&A sessions\n• Corporate training and team sessions\n• Live coding demonstrations\n\nI have professional streaming setup for high-quality virtual events.`
        },
        {
          question: 'What are your speaking fees?',
          answer: `Speaking fees vary by event type and scope:\n• Community events: Often free or travel expenses only\n• Corporate workshops: $2,500-5,000 per day\n• Conference keynotes: $5,000-10,000\n• Virtual events: $1,500-3,000\n\nNon-profit and educational discounts available.`
        },
        {
          question: 'How far in advance should we book?',
          answer: `Booking timeline recommendations:\n• Major conferences: 3-6 months in advance\n• Corporate events: 6-8 weeks minimum\n• Virtual events: 4-6 weeks preferred\n• Emergency/last-minute: Contact me directly\n\nEarlier booking ensures better availability and preparation time.`
        }
      ]
    },
    {
      title: 'Community & Partnerships',icon: 'Heart',
      questions: [
        {
          question: 'How can I collaborate with CodersHub?',answer: `Multiple collaboration opportunities with CodersHub:\n• Guest content creation and tutorials\n• Co-hosting events and workshops\n• Mentorship program partnerships\n• Sponsorship and brand partnerships\n• Community challenges and hackathons\n\nWe're always open to mutually beneficial partnerships.`
        },
        {
          question: 'Do you offer content collaboration?',
          answer: `Yes! I love creating valuable content with others:\n• Guest blog posts and technical articles\n• Podcast interviews and discussions\n• YouTube collaborations and tutorials\n• Social media partnerships\n• Course creation and educational content\n\nLet's discuss how we can create something amazing together.`
        },
        {
          question: 'Can you help with our developer community?',answer: `I'd love to help grow your developer community:\n• Community strategy and planning\n• Event planning and execution\n• Mentorship program development\n• Content creation and curation\n• Member engagement strategies\n• Growth and retention tactics`
        },
        {
          question: 'What about open source contributions?',
          answer: `I'm passionate about open source:\n• Contributing to existing projects\n• Collaborating on new initiatives\n• Code reviews and technical guidance\n• Documentation and community building\n• Sponsorship of promising projects\n\nCheck out my GitHub for current contributions and interests.`
        }
      ]
    }
  ];

  const toggleItem = (categoryIndex, questionIndex) => {
    const itemId = `${categoryIndex}-${questionIndex}`;
    const newOpenItems = new Set(openItems);
    
    if (newOpenItems?.has(itemId)) {
      newOpenItems?.delete(itemId);
    } else {
      newOpenItems?.add(itemId);
    }
    
    setOpenItems(newOpenItems);
  };

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Find answers to common questions about working together, mentorship, and collaboration opportunities.
          </p>
        </div>

        <div className="space-y-8">
          {faqCategories?.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-card rounded-xl border border-border overflow-hidden">
              {/* Category Header */}
              <div className="bg-muted/50 px-6 py-4 border-b border-border">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={category?.icon} size={20} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{category?.title}</h3>
                </div>
              </div>

              {/* Questions */}
              <div className="divide-y divide-border">
                {category?.questions?.map((item, questionIndex) => {
                  const itemId = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openItems?.has(itemId);

                  return (
                    <div key={questionIndex}>
                      <button
                        onClick={() => toggleItem(categoryIndex, questionIndex)}
                        className="w-full px-6 py-4 text-left hover:bg-muted/30 transition-colors duration-200 focus:outline-none focus:bg-muted/30"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-medium text-foreground pr-4">
                            {item?.question}
                          </h4>
                          <Icon
                            name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                            size={20}
                            className="text-muted-foreground transition-transform duration-200"
                          />
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <div className="prose prose-sm max-w-none">
                            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                              {item?.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-12 text-center">
          <div className="bg-card rounded-xl border border-border p-8">
            <div className="max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MessageCircle" size={32} className="text-primary" />
              </div>
              
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                Still Have Questions?
              </h3>
              
              <p className="text-muted-foreground mb-6">
                Can't find what you're looking for? I'm here to help! Send me a message and I'll get back to you within 24 hours with a personalized response.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:devcodead0@gmail.c?subject=Question from the FAQ page"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-300"
                >
                  <Icon name="Mail" size={20} className="mr-2" />
                  Send Email
                </a>
                
                <a
                  href="#contact-form"
                  className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg hover:bg-muted/50 transition-colors duration-300"
                >
                  <Icon name="MessageSquare" size={20} className="mr-2" />
                  Use Contact Form
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;